/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbReactServices } from "abreact/service/AbReactServices";
import { AbWebServices } from "abweb/service/AbWebServices";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { ModuleLoaderUtils } from "abweb/utils/ModuleLoaderUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
const LOG = LogUtils.getLogger(__filename);
class PreloadLoginManager {
  constructor(baseLoginFn, alProps, preloadSpecs, alwaysPreloadLastSpec = false)
  // private readonly prefetchStrategy: ("sequential" | "parallel") = "sequential")
  {
    this.baseLoginFn = baseLoginFn;
    this.alProps = alProps;
    this.preloadSpecs = preloadSpecs;
    this.alwaysPreloadLastSpec = alwaysPreloadLastSpec;
  }
  async login() {
    if (!AbReactServices.isInitialized() || !AbWebServices.isInitialized()) {
      throw new Error("AbWebServices and AbReactServices must be initialized before invoking PreloadLoginManager::login");
    }
    this.alProps.profileStartup("Before preload login dialog");
    const rawLoginP = this.baseLoginFn();
    // preloadModules returns the rawLoginP, so will not complete until baseLoginFn has successfully completed.
    await this.preloadModules(rawLoginP);
    this.alProps.profileStartup("After preload login dialog");
  }

  /**
   * Preload modules while controlling promise (e.g., login dialog promise) is still 'running'.
   * Preloading will finish as soon as it detects that controlling promise has settled (resolved or rejected)
   *
   * @param controllingPromise
   * @returns controllingPromise - may be still running, or settled.
   */
  async preloadModules(controllingPromise) {
    // wrap login dlg promise so that we can tell when it is settled (either resolver or reject)
    let controllingPromiseSettled = false;
    controllingPromise.then(() => controllingPromiseSettled = true, () => controllingPromiseSettled = true);
    if (controllingPromiseSettled) {
      return controllingPromise;
    }

    // Use loginMutex to avoid jsessionid race condition between login rpc request with module preloading requests.
    // See ABSDK-721 for more details.
    const loginMutex = AbWebServices.authenticationService.loginMutex;

    // Preloading strategy discussion:
    // Note do not want loginMutex to wrap a *batch* of preimports, as that would prevent login rpc until ALL
    // preimports in the 'batch' totally finished.  Want login rpc to be able to happen in middle of preloads,
    // so use fine-grained 'single preimport in flight' approach, which allows for more responsive user keystroke import,
    // and more responsive login rpc submits.
    for (let specIndex = 0; specIndex < this.preloadSpecs.length; specIndex++) {
      await this.importSpec(specIndex, loginMutex);

      // Cooperative yield to allow user input processing (e.g., username/password keystrokes in login dialog).
      // Probably not needed as just returned from await above, but just to be extra nice
      await new Promise(resolve => setTimeout(resolve));

      // if the login promise is resolved - load the last module and break from the loop
      if (controllingPromiseSettled) {
        this.alProps.profileStartup(`Login promise settled`);

        // if we were given the startup function, we need to import the last module from the list
        // because it is passed to the startup function as a parameter. If we don't have the startup
        // function, we don't need to import the last module because it will be imported by an
        // application specific script.
        const lastSpecIndex = this.preloadSpecs.length - 1;
        if (this.alwaysPreloadLastSpec && specIndex !== lastSpecIndex /* just loaded last one */) {
          this.alProps.profileStartup(`Skip to preloading last module`);
          await this.importSpec(lastSpecIndex, loginMutex);
        }
        break;
      }
    }
    this.alProps.profileStartup("Finished preloading modules");

    // Two possibilities:
    // 1. module preloading is done, so return controllingPromise (which may or may not be settled)
    // 2. module preloading was stopped 'early' as controllingPromise settled
    return controllingPromise;
  }
  async importSpec(specIndex, loginMutex) {
    const preloadSpec = this.preloadSpecs[specIndex];
    const moduleName = preloadSpec.exportName ?? "PreloadSpec[" + specIndex + "]";
    try {
      let startMSec = 0;
      // Use loginMutex to avoid jsessionid race condition with background module pre-loading.  See ABSDK-721
      await loginMutex.runExclusive("Preloading module: " + moduleName, () => {
        startMSec = performance.now(); // Put inside mutex as don't care about mutex queueing time
        return ModuleLoaderUtils.import(preloadSpec); // ModuleLoader records import profiling (pfs)
      });
      const elapsed = NumberUtils.FORMAT_DECIMAL_2(performance.now() - startMSec);
      this.alProps.profileStartup(`Preloaded module: ${moduleName} in ${elapsed} msec`);
      return true;
    } catch (err) {
      LOG.error(`Error while preloading module '${moduleName}': ${err}`);
    }
    return false;
  }
}
export function createPreloadLoginFn(uiLoginDlg, alProps, preloadSpecs, alwaysPreloadLastSpec = false) {
  if (preloadSpecs.length === 0) {
    return () => uiLoginDlg({});
  }
  let preloadMgr = new PreloadLoginManager(uiLoginDlg, alProps, preloadSpecs, alwaysPreloadLastSpec);
  return preloadMgr.login.bind(preloadMgr);
}
//# sourceMappingURL=PreloadLoginManager.js.map
